import * as React from 'react';
import {
    ArrowDownFromLine,
    ArrowLeftFromLine,
    ArrowRightFromLine,
    ArrowUpFromLine,
    Merge,
    TableCellsSplit,
    TableProperties,
    Trash2,
} from 'lucide-react';
import { useCurrentEditor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { circularDeepEqual } from 'fast-equals';
import { cn } from '../../../lib/utils';
import { kitT, type KitTranslateFn } from '../../../lib/kitTranslate';
import { ToolbarButton } from './ToolbarButton';

export interface TableBubbleMenuProps {
    translate?: KitTranslateFn;
}

export const TableBubbleMenu: React.FC<TableBubbleMenuProps> = ({ translate }) => {
    const { editor } = useCurrentEditor();
    // WHY: Selection carries a circular reference to Node.type.schema; the
    // default plain deepEqual used by useEditorState recurses into that
    // cycle and throws "Maximum call stack size exceeded" on every selection
    // change (every keystroke), crashing the essay editor in the mobile
    // WebView. circularDeepEqual (fast-equals) is cycle-safe.
    useEditorState({
        editor,
        selector: (ctx) => ctx.editor?.state.selection ?? null,
        equalityFn: circularDeepEqual,
    });

    const tx = (k: string, fb: string) => kitT(translate, k, fb);

    if (!editor) return null;

    const lt = (key: string, fb: string) => {
        const s = tx(key, fb);
        return { 'aria-label': s, title: s } as const;
    };

    return (
        <BubbleMenu
            editor={editor}
            pluginKey="tableBubbleMenu"
            updateDelay={0}
            resizeDelay={0}
            className={cn(
                'rich-text-table-bubble-menu flex flex-wrap items-center gap-0.5 rounded-md border border-border bg-popover p-1 shadow-md'
            )}
            shouldShow={({ editor: ed }) => ed.isEditable && ed.isActive('table')}
        >
            <div className="rich-text-table-bubble-menu-row-group flex items-center gap-0.5">
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().addRowBefore().run()}
                    disabled={!editor.can().addRowBefore()}
                    {...lt('kRtmAddRowBefore', 'Add row before')}
                >
                    <ArrowUpFromLine className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                    disabled={!editor.can().addRowAfter()}
                    {...lt('kRtmAddRowAfter', 'Add row after')}
                >
                    <ArrowDownFromLine className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().deleteRow().run()}
                    disabled={!editor.can().deleteRow()}
                    {...lt('kRtmDeleteRow', 'Delete row')}
                >
                    <Trash2 className="h-4 w-4" />
                </ToolbarButton>
            </div>

            <div
                className="rich-text-table-bubble-menu-separator w-px h-6 bg-border"
                aria-hidden
            />

            <div className="rich-text-table-bubble-menu-col-group flex items-center gap-0.5">
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().addColumnBefore().run()}
                    disabled={!editor.can().addColumnBefore()}
                    {...lt('kRtmAddColBefore', 'Add column before')}
                >
                    <ArrowLeftFromLine className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().addColumnAfter().run()}
                    disabled={!editor.can().addColumnAfter()}
                    {...lt('kRtmAddColAfter', 'Add column after')}
                >
                    <ArrowRightFromLine className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                    disabled={!editor.can().deleteColumn()}
                    {...lt('kRtmDeleteCol', 'Delete column')}
                >
                    <Trash2 className="h-4 w-4" />
                </ToolbarButton>
            </div>

            <div
                className="rich-text-table-bubble-menu-separator w-px h-6 bg-border"
                aria-hidden
            />

            <div className="rich-text-table-bubble-menu-cell-group flex items-center gap-0.5">
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().mergeCells().run()}
                    disabled={!editor.can().mergeCells()}
                    {...lt('kRtmMerge', 'Merge cells')}
                >
                    <Merge className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().splitCell().run()}
                    disabled={!editor.can().splitCell()}
                    {...lt('kRtmSplit', 'Split cell')}
                >
                    <TableCellsSplit className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                    disabled={!editor.can().toggleHeaderRow()}
                    {...lt('kRtmToggleHeaderRow', 'Toggle header row')}
                >
                    <TableProperties className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
                    disabled={!editor.can().toggleHeaderColumn()}
                    {...lt('kRtmToggleHeaderCol', 'Toggle header column')}
                >
                    <TableProperties className="h-4 w-4 rotate-90" />
                </ToolbarButton>
            </div>

            <div
                className="rich-text-table-bubble-menu-separator w-px h-6 bg-border"
                aria-hidden
            />

            <div className="rich-text-table-bubble-menu-table-group flex items-center gap-0.5">
                <ToolbarButton
                    className="p-1.5"
                    onClick={() => editor.chain().focus().deleteTable().run()}
                    disabled={!editor.can().deleteTable()}
                    {...lt('kRtmDeleteTable', 'Delete table')}
                >
                    <Trash2 className="h-4 w-4 text-destructive" />
                </ToolbarButton>
            </div>
        </BubbleMenu>
    );
};

TableBubbleMenu.displayName = 'TableBubbleMenu';
